// ShareThreadDialog — owner-only "share this chat" dialog.
// Sharing copies the transcript into a brand-new thread owned by the
// recipient — safe and isolated, no ongoing access grant. Debounced user
// search (useSearchShareUsers) + a "Share" action, matching OwnersTab's
// user-picker UX but without a persistent list to manage (there's nothing
// to revoke). Externally controlled (isOpen/onClose) so the caller can
// drive it from an ActionMenu item rather than a built-in trigger button —
// same controlled-dialog convention as
// components/settings/agent/PublishConfirmDialog.tsx.
//
// The user search dropdown is a plain Input + absolutely-positioned result
// list (no shadcn combobox is installed) — avoids pulling in a new combobox
// dependency for one search field.

import { useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { cn } from '@/shared/lib/cn';
import { useCurrentUserQuery } from '@/entities/user';
import type { UserOptionView } from '@/entities/owner';
import { useShareThread } from '@/entities/thread';
import { useSearchShareUsers } from '../model/useSearchShareUsers';

function userOptionLabel(u: UserOptionView): string {
  return u.displayName && u.displayName !== u.email ? `${u.displayName} <${u.email}>` : u.email;
}

function ShareThreadDialogContent({ threadId, close }: { threadId: string; close: () => void }) {
  const currentUserId = useCurrentUserQuery().data?.id ?? null;
  const { shareThread, isPending } = useShareThread();
  const search = useSearchShareUsers();

  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selection, setSelection] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sharedWith, setSharedWith] = useState<string[]>([]);

  const onInputChange = (q: string) => {
    setQuery(q);
    setShowResults(true);
    setSelection(null);
    setError(null);
    search.search(q);
  };

  const handleShare = async () => {
    if (!selection) return;
    const target = search.results.find((u) => u.userId === selection);
    if (!target) return;
    if (target.userId === currentUserId) {
      setError('You cannot share a thread with yourself.');
      return;
    }
    setError(null);
    try {
      await shareThread({ threadId, userId: target.userId });
      setSharedWith((prev) => [...prev, userOptionLabel(target)]);
      setQuery('');
      setSelection(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share thread');
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Share this chat</DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-2">
        {error && (
          <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          Share an independent copy of this chat.
        </p>

        <div className="flex items-end gap-2">
          <div className="relative flex-1">
            <Label htmlFor="share-thread-search">Share with</Label>
            <Input
              id="share-thread-search"
              aria-label="Search users to share with"
              value={query}
              onChange={(e) => onInputChange(e.target.value)}
              onFocus={() => setShowResults(true)}
              onBlur={() => {
                // Allow a click on a result row to register before closing.
                window.setTimeout(() => setShowResults(false), 150);
              }}
              placeholder="Type 2+ characters to search @adobe.com users."
              className="mt-1"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Not in search means not registered.
            </p>
            {showResults && query.length > 0 && (
              <div className="absolute top-full left-0 z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-border bg-popover shadow-md">
                {search.loading ? (
                  <div className="p-3 text-xs text-muted-foreground">Searching…</div>
                ) : search.results.length === 0 ? (
                  <div className="p-3 text-xs text-muted-foreground">No matches. User is not registered.</div>
                ) : (
                  search.results.map((u) => (
                    <button
                      key={u.userId}
                      type="button"
                      className={cn(
                        'block w-full px-3 py-2 text-left text-sm hover:bg-muted',
                        selection === u.userId && 'bg-muted',
                      )}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSelection(u.userId);
                        setQuery(userOptionLabel(u));
                        setShowResults(false);
                      }}
                    >
                      {userOptionLabel(u)}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <Button onClick={handleShare} disabled={!selection || isPending}>
            {isPending ? 'Sharing…' : 'Share'}
          </Button>
        </div>

        {sharedWith.length > 0 && (
          <p className="text-sm text-muted-foreground">Shared a copy with: {sharedWith.join(', ')}</p>
        )}
      </div>

      <DialogFooter>
        <Button variant="secondary" onClick={close}>
          Done
        </Button>
      </DialogFooter>
    </>
  );
}

export function ShareThreadDialog({
  threadId,
  isOpen,
  onClose,
}: {
  threadId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent aria-label="Share this chat">
        {isOpen && <ShareThreadDialogContent threadId={threadId} close={onClose} />}
      </DialogContent>
    </Dialog>
  );
}
